import AngleLeft from "@icons/angle-left.svg?react";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import isFinite from "lodash-es/isFinite";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  generatePath,
  Link,
  Outlet,
  resolvePath,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import invariant from "tiny-invariant";
import { z } from "zod";

import { traderSchema } from "@/api/types/digitalnisklady.cz/types";
import {
  canSignCheckFn,
  useGuard,
} from "@/common/acl/action-guard/action-guard";
import { BindingOfferSellDialog } from "@/common/components/binding-offer-dialog/binding-offer-sell-dialog";
import { Button, primary, secondary } from "@/common/components/button/button";
import { InputField } from "@/common/components/form/input-field/input-field";
import { Metadata } from "@/common/components/metadata/metadata";
import {
  trackTradingSellCancelled,
  trackTradingSellClick,
  trackTradingSellConfirmed,
} from "@/common/components/mixpanel/mixpanel-tracking";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { entitySchema } from "@/common/schema/entity";
import { currencyFormatter } from "@/common/services/formatter";

import { useConfirmedAction } from "../admin/components/user/user";
import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { getAbsolutePath } from "../routes.utils";
import { handleFormError } from "../shared/api/error-handler";
import { path as transactionsAndOrdersPath } from "../transactions-and-orders/transactions-and-orders";
import { useAddContractMutation } from "./trades.api";

type Fields = {
  customTransport: boolean;
  customTransportNote?: string;
  note: string;
  amount: number;
};

const path = "sell";
const TradesDetailSell = () => {
  const { _ } = useLingui();
  const [, currency] = useCurrency();
  const { offerId } = useParams();
  invariant(offerId);
  const context = useTradesDetailSellContext();
  const { postalCode } = context;
  const form = useForm<Fields>();
  const hasCustomTransport = form.watch("customTransport");
  const customTransportNote = form.watch("customTransportNote");
  const addContract = useAddContractMutation();
  const navigate = useNavigate();
  const { runWhenConfirmed, dialog, setDialog, rejectRef, resolveRef } =
    useConfirmedAction();
  const amount = form.watch("amount");
  const note = form.watch("note");
  const totalPrice = isFinite(amount)
    ? amount * context.marketPrice.price
    : undefined;

  const handleSubmit = ({ customTransportNote, ...data }: Fields) => {
    trackTradingSellClick({
      crop: context.crop.name,
      currency,
      price: context.marketPrice.price,
      quarter: context.quarter.name,
    });
    addContract.mutate(
      {
        ...data,
        // @ts-expect-error discrepancy between CF API and IDSign API
        currency,

        marketPriceId: context.marketPrice.id,

        offerId,
        transportNote: customTransportNote,
      },
      {
        onError: (e) => {
          setDialog(undefined);
          const errors = handleFormError(e);

          errors.forEach((error) => {
            if (error.field.includes("contract")) {
              form.setError("amount", { message: error.error.join("\n") });
            }
          });
        },
        onSuccess: async (data) => {
          setDialog(undefined);
          await navigate("../success/contract/" + data.addContract?.id);
        },
      },
    );
  };
  const guardedSubmit = useGuard({
    action: handleSubmit,
    checkFn: canSignCheckFn,
  });

  React.useEffect(() => {
    if (!hasCustomTransport && customTransportNote) {
      form.setValue("customTransportNote", undefined);
    }
  }, [hasCustomTransport, customTransportNote, form]);

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink replace to="..">
            <AngleLeft />
          </SearchParamsLink>
        }
      >
        <Metadata title={_(msg`Prodat komoditu`)} />
        <FullscreenModalLayout.Title>
          <Trans>Prodat komoditu</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[
            {
              label: <Trans>Komodita</Trans>,
              value: context.crop.name,
            },
            {
              label: <Trans>Kvartál</Trans>,
              value: context.quarter.name,
            },
            {
              label: <Trans>Farma</Trans>,
              value: context.company.name,
            },
            {
              label: <Trans>Sklad</Trans>,
              value: context.storage.name,
            },
            {
              label: <Trans>Celková cena</Trans>,
              value: totalPrice
                ? currencyFormatter(currency).format(totalPrice)
                : "-",
            },
            {
              label: <Trans>Obchodník</Trans>,
              value: (
                <img src={context.trader.logo} style={{ height: "20px" }} />
              ),
            },
          ]}
        />
        <Spacer className="h-6" />
        <FormProvider {...form}>
          <form id="sell-form" onSubmit={form.handleSubmit(guardedSubmit)}>
            <fieldset disabled={addContract.status === "pending"}>
              <InputField<Fields>
                autoComplete="off"
                label={<Trans>Množství v tunách</Trans>}
                name="amount"
                options={{
                  max: {
                    message: t`Množství je vyšší než dostupné množství ve skladu`,
                    value: context.offer.amount,
                  },
                  min: {
                    message: t`Množství musí být vyšší než 24 tun`,
                    value: 24,
                  },
                  required: t`Vyplňte prosím množství`,
                  valueAsNumber: true,
                }}
                step="0.1"
                type="number"
              />
              <Spacer className="h-6" />
              <label
                className="relative flex h-6 justify-between"
                htmlFor="custom-transport"
              >
                <Trans>Vlastní doprava</Trans>
                <input
                  {...form.register("customTransport")}
                  role="switch"
                  type="checkbox"
                />
              </label>
              {hasCustomTransport ? (
                <>
                  <Spacer className="h-6" />
                  <label className="" htmlFor="custom-transport-note">
                    <Trans>Poznámka k dopravě</Trans>
                    <textarea
                      className="mt-2 w-full rounded-lg"
                      {...form.register("customTransportNote")}
                    />
                  </label>
                </>
              ) : null}
              <Spacer className="h-6" />
              <label htmlFor="note">
                <Trans>Poznámka</Trans>
                <textarea
                  className="mt-2 w-full rounded-lg"
                  {...form.register("note")}
                  id="note"
                />
              </label>
            </fieldset>
          </form>
        </FormProvider>
        <Button.Container>
          <SearchParamsLink className={secondary} to="order">
            <Trans>Nastavit příkaz k prodeji</Trans>
          </SearchParamsLink>
          <Button
            disabled={addContract.status === "pending"}
            onClick={async () => {
              const isValid = await form.trigger();
              if (isValid) {
                runWhenConfirmed(
                  <Trans>
                    Přejete si potvrdit závaznou nabídku a zboží prodat?
                  </Trans>,
                  () => {
                    const formData = form.getValues();
                    guardedSubmit(formData);
                  },
                )();
              }
            }}
            type="button"
            variant="primary"
          >
            <Trans>Prodat za</Trans>{" "}
            {currencyFormatter(currency).format(context.marketPrice.price)}
          </Button>
        </Button.Container>
      </FullscreenModalLayout>
      <Outlet
        context={{
          ...context,
          customTransportNote,
          hasCustomTransport,
          note,
          tons: isFinite(form.watch("amount"))
            ? form.watch("amount")
            : undefined,
        }}
      />
      {dialog ? (
        <BindingOfferSellDialog
          amount={form.getValues("amount") + " t"}
          cropName={context.crop.name}
          isPending={addContract.status === "pending"}
          onCancel={() => {
            rejectRef.current?.();
            trackTradingSellCancelled({
              crop: context.crop.name,
              currency,
              price: context.marketPrice.price,
              quarter: context.quarter.name,
            });
          }}
          onConfirm={(e) => {
            resolveRef.current?.(e);
            trackTradingSellConfirmed({
              crop: context.crop.name,
              currency,
              price: context.marketPrice.price,
              quarter: context.quarter.name,
            });
          }}
          postalCodeLabel={postalCode}
          quarter={context.quarter.name}
          timeFromTrading={context.trader.timeFromTrading}
          timeToTrading={context.trader.timeToTrading}
          tocUrl={context.trader.tocUrl}
          traderName={context.trader.name}
          unitPrice={currencyFormatter(currency).format(
            context.marketPrice.price,
          )}
        />
      ) : null}
    </>
  );
};

const TransactionLink = () => {
  const { contractId } = useParams();
  invariant(contractId);

  return (
    <Link
      className={primary}
      replace
      to={resolvePath(
        generatePath("transactions/:contractId", { contractId }),
        getAbsolutePath(transactionsAndOrdersPath),
      )}
    >
      <Trans>Přejít na detail příkazu</Trans>
    </Link>
  );
};

const contextSchema = z.object({
  company: entitySchema,
  crop: entitySchema,
  marketPrice: z.object({
    id: z.string(),
    price: z.number(),
  }),
  offer: z.object({
    amount: z.number(),
  }),
  postalCode: z.string(),
  quarter: entitySchema,
  storage: entitySchema,
  trader: traderSchema,
  traders: z.array(traderSchema),
});

const useTradesDetailSellContext = () => {
  const context = useOutletContext();

  return contextSchema.parse(context);
};

export { contextSchema, path, TradesDetailSell, TransactionLink };
